<template>
  <div class="chart-container">
    <TimeRangeFilter
      chartId="job-applications-subscription"
      @range-changed="handleRangeChange"
    />
    <div v-if="isLoading" class="chart-loading">
      <div class="chart-spinner"></div>
    </div>
    <canvas v-show="!isLoading" ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import Chart from 'chart.js/auto'
import TimeRangeFilter from '@/components/charts/TimeRangeFilter.vue'
import apiClient from '@/services/api'
import { useTheme } from '@/composables/useTheme'

// Resolves a themed HSL custom property (e.g. "--foreground") into a real
// color string Chart.js/canvas can render, honoring light/dark mode.
function themeColor(varName, alpha) {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim()
  if (!value) return alpha != null ? `hsla(0, 0%, 50%, ${alpha})` : '#64748b'
  return alpha != null ? `hsl(${value} / ${alpha})` : `hsl(${value})`
}

export default {
  name: 'JobApplicationsBySubscriptionChart',
  components: { TimeRangeFilter },
  setup() {
    const chartCanvas = ref(null)
    const timeRange = ref('day')
    const isLoading = ref(false)
    const { theme } = useTheme()
    let chart = null
    let lastData = null

    const getChartTitle = () => {
      switch (timeRange.value) {
        case 'week': return 'Applications by Subscription — Weekly'
        case 'month': return 'Applications by Subscription — Monthly'
        default: return 'Applications by Subscription — Daily'
      }
    }

    const getXAxisTitle = () => {
      switch (timeRange.value) {
        case 'week': return 'Week'
        case 'month': return 'Month'
        default: return 'Day'
      }
    }

    const createChart = (data) => {
      if (!chartCanvas.value) return
      if (chart) { chart.destroy(); chart = null }
      lastData = data

      const ctx = chartCanvas.value.getContext('2d')
      const foreground = themeColor('--foreground')
      const mutedForeground = themeColor('--muted-foreground')
      const gridColor = themeColor('--border', 0.5)
      chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: data.labels || [],
          datasets: [
            {
              label: 'Paying',
              data: data.paying || [],
              backgroundColor: 'rgba(99, 102, 241, 0.85)',
              borderColor: '#6366f1',
              borderWidth: 1,
              borderRadius: 4,
              borderSkipped: false,
            },
            {
              label: 'Trial',
              data: data.trial || [],
              backgroundColor: 'rgba(59, 130, 246, 0.7)',
              borderColor: '#3b82f6',
              borderWidth: 1,
              borderRadius: 4,
              borderSkipped: false,
            },
            {
              label: 'Expired / Not Subscribed',
              data: data.expired || [],
              backgroundColor: 'rgba(148, 163, 184, 0.7)',
              borderColor: '#94a3b8',
              borderWidth: 1,
              borderRadius: 4,
              borderSkipped: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: getChartTitle(),
              font: { size: 18, weight: 'bold' },
              color: foreground,
            },
            legend: {
              display: true,
              position: 'top',
              labels: { usePointStyle: true, padding: 20, color: mutedForeground, font: { size: 14 } },
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(0,0,0,0.8)',
              titleColor: '#ffffff',
              bodyColor: '#ffffff',
              callbacks: {
                footer(items) {
                  const total = items.reduce((s, i) => s + i.parsed.y, 0)
                  return `Total: ${total}`
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              title: {
                display: true,
                text: getXAxisTitle(),
                font: { size: 14, weight: '600' },
                color: mutedForeground,
              },
              grid: { color: gridColor },
              ticks: { color: mutedForeground, font: { size: 12 } },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              title: {
                display: true,
                text: 'Applications',
                font: { size: 14, weight: '600' },
                color: mutedForeground,
              },
              grid: { color: gridColor },
              ticks: {
                color: mutedForeground,
                font: { size: 12 },
                stepSize: 1,
                callback: (v) => Math.round(v),
              },
            },
          },
        },
      })
    }

    const updateChart = (data) => {
      if (!chart) return
      lastData = data
      chart.data.labels = data.labels || []
      chart.data.datasets[0].data = data.paying || []
      chart.data.datasets[1].data = data.trial || []
      chart.data.datasets[2].data = data.expired || []
      chart.options.plugins.title.text = getChartTitle()
      chart.options.scales.x.title.text = getXAxisTitle()
      chart.update()
    }

    const fetchData = async () => {
      isLoading.value = true
      try {
        const res = await apiClient.get('/admin-dashboard/job-applications-by-subscription/', {
          params: { period: timeRange.value },
        })
        if (chart) {
          updateChart(res.data)
        } else {
          createChart(res.data)
        }
      } catch (err) {
        console.error('Error fetching subscription applications data:', err)
      } finally {
        isLoading.value = false
      }
    }

    const handleRangeChange = async (event) => {
      timeRange.value = event.timeRange
      await fetchData()
    }

    onMounted(fetchData)
    onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })

    // Rebuild the chart when the theme toggles so canvas text/grid colors
    // (which can't respond to CSS custom properties on their own) stay legible.
    watch(theme, () => {
      if (lastData) createChart(lastData)
    })

    return { chartCanvas, isLoading, handleRangeChange }
  },
}
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 400px;
  max-height: 400px;
  position: relative;
  background: hsl(var(--card));
  color: hsl(var(--foreground));
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid hsl(var(--border));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chart-container canvas {
  flex: 1;
  min-height: 0;
}

.chart-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid hsl(var(--border));
  border-top-color: hsl(var(--primary));
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .chart-container {
    height: 350px;
    max-height: 350px;
    padding: 1rem;
  }
}
</style>
